import type { SOC2Compliance } from '@/hooks/useReportActions';

const getScoreColor = (score: number) => {
  if (score >= 90) return '#16a34a'; // green-600
  if (score >= 70) return '#ca8a04'; // yellow-600
  if (score >= 50) return '#ea580c'; // orange-600
  return '#dc2626'; // red-600
};

export const createSOC2HeaderHTML = (soc2: SOC2Compliance) => {
  return `
  <div style="padding: 20px; background: white;">
    <div style="border-bottom: 2px solid #e5e7eb; padding-bottom: 12px; margin-bottom: 20px;">
      <h2 style="font-size: 24px; font-weight: 700; color: #1f2937; margin: 0 0 8px 0;">SOC2 Technical Compliance</h2>
      <p style="font-size: 14px; color: #6b7280; margin: 0;">Automated assessment of technical security controls</p>
    </div>

    <div style="background: #f9fafb; border: 1px solid #e5e7eb; border-radius: 8px; padding: 20px; margin-bottom: 20px;">
      <div style="text-align: center;">
        <div style="font-size: 48px; font-weight: 700; color: ${getScoreColor(soc2.overall_technical_compliance_score)}; margin-bottom: 8px;">
          ${soc2.overall_technical_compliance_score.toFixed(1)}%
        </div>
        <div style="font-size: 16px; color: #6b7280; margin-bottom: 20px;">Overall Technical Compliance Score</div>
      </div>

      <div style="display: grid; grid-template-columns: repeat(3, 1fr); gap: 16px; margin-top: 20px;">
        <div style="background: white; border: 1px solid #e5e7eb; border-radius: 8px; padding: 12px; text-align: center;">
          <div style="font-size: 12px; color: #6b7280; margin-bottom: 4px;">Controls Scanned</div>
          <div style="font-size: 24px; font-weight: 700; color: #1f2937;">${soc2.controls_scanned}</div>
        </div>
        <div style="background: white; border: 1px solid #e5e7eb; border-radius: 8px; padding: 12px; text-align: center;">
          <div style="font-size: 12px; color: #6b7280; margin-bottom: 4px;">Total Findings</div>
          <div style="font-size: 24px; font-weight: 700; color: #1f2937;">${soc2.total_vulnerabilities}</div>
        </div>
        <div style="background: white; border: 1px solid #e5e7eb; border-radius: 8px; padding: 12px; text-align: center;">
          <div style="font-size: 12px; color: #6b7280; margin-bottom: 4px;">Manual Review Required</div>
          <div style="font-size: 24px; font-weight: 700; color: #1f2937;">${soc2.organizational_controls.length}</div>
        </div>
      </div>
    </div>

    <div style="background: #eff6ff; border-left: 4px solid #3b82f6; padding: 12px; border-radius: 4px;">
      <div style="font-size: 12px; color: #1e40af; line-height: 1.5;">
        ${soc2.disclaimer}
      </div>
    </div>
  </div>
  `;
};

export const createSOC2ControlHTML = (control: any) => {
  return `
  <div style="padding: 8px 20px; background: white;">
    <div style="border: 1px solid #e5e7eb; border-radius: 8px; padding: 12px; background: white;">
      <div style="display: flex; justify-content: space-between; align-items: start; margin-bottom: 6px;">
        <div style="flex: 1;">
          <div style="font-size: 13px; font-weight: 600; color: #1f2937; margin-bottom: 3px;">
            ${control.id} - ${control.name}
          </div>
          <div style="font-size: 11px; color: #6b7280; margin-bottom: 6px;">
            ${control.description}
          </div>
        </div>
        <div style="font-size: 16px; font-weight: 700; color: ${getScoreColor(control.score)}; margin-left: 16px;">
          ${control.score.toFixed(1)}%
        </div>
      </div>

      ${control.vulnerability_count > 0 ? `
        <div style="margin-top: 12px; padding-top: 12px; border-top: 1px solid #e5e7eb;">
          <div style="font-size: 12px; font-weight: 500; color: #1f2937; margin-bottom: 8px;">
            ${control.vulnerability_count} finding${control.vulnerability_count !== 1 ? 's' : ''}
          </div>
          <div style="display: flex; gap: 8px; flex-wrap: wrap;">
            ${control.severity_breakdown.critical > 0 ? `
              <span style="background: #fee2e2; color: #991b1b; padding: 2px 8px; border-radius: 4px; font-size: 11px; font-weight: 500;">
                ${control.severity_breakdown.critical} Critical
              </span>
            ` : ''}
            ${control.severity_breakdown.high > 0 ? `
              <span style="background: #fee2e2; color: #991b1b; padding: 2px 8px; border-radius: 4px; font-size: 11px; font-weight: 500;">
                ${control.severity_breakdown.high} High
              </span>
            ` : ''}
            ${control.severity_breakdown.medium > 0 ? `
              <span style="background: #fef3c7; color: #92400e; padding: 2px 8px; border-radius: 4px; font-size: 11px; font-weight: 500;">
                ${control.severity_breakdown.medium} Medium
              </span>
            ` : ''}
            ${control.severity_breakdown.low > 0 ? `
              <span style="background: #e0e7ff; color: #3730a3; padding: 2px 8px; border-radius: 4px; font-size: 11px; font-weight: 500;">
                ${control.severity_breakdown.low} Low
              </span>
            ` : ''}
            ${control.severity_breakdown.info > 0 ? `
              <span style="background: #f3f4f6; color: #374151; padding: 2px 8px; border-radius: 4px; font-size: 11px; font-weight: 500;">
                ${control.severity_breakdown.info} Info
              </span>
            ` : ''}
          </div>
        </div>
      ` : `
        <div style="display: flex; align-items: center; gap: 8px; margin-top: 8px; color: #16a34a; font-size: 12px;">
          <span style="font-weight: 500;">✓ No issues found</span>
        </div>
      `}
    </div>
  </div>
  `;
};

export const createSOC2OrganizationalHTML = (soc2: SOC2Compliance) => {
  return `
  <div style="padding: 20px; background: white;">
    <div>
      <h3 style="font-size: 18px; font-weight: 600; color: #1f2937; margin: 0 0 12px 0;">Organizational Controls - Manual Review Required</h3>
      <p style="font-size: 12px; color: #6b7280; margin: 0 0 16px 0;">These controls require documentation and policy review</p>

      ${soc2.organizational_controls.map(control => `
        <div style="border: 1px solid #e5e7eb; border-radius: 8px; padding: 12px; margin-bottom: 8px; background: white;">
          <div style="font-size: 13px; font-weight: 600; color: #1f2937; margin-bottom: 4px;">
            ${control.id} - ${control.name}
          </div>
          <div style="font-size: 11px; color: #6b7280; margin-bottom: 4px;">
            <strong>Required:</strong> ${control.requires}
          </div>
          <div style="font-size: 11px; color: #6b7280;">
            <strong>Examples:</strong> ${control.examples}
          </div>
        </div>
      `).join('')}
    </div>
  </div>
  `;
};
